"use client";
import React, { useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import Alert from "@/components/Alert";
import { useLanguageContext } from "@/context/LanguageContext";

interface NewerVersionDocumentsBannerProps {
  /**
   * Whether the vendor is an existing vendor
   */
  isExistingVendor?: boolean;
  
  /**
   * Custom message to display. If not provided, uses the default from language text
   */
  message?: string;
  
  /**
   * Custom className for the banner container to apply component-specific styling
   */
  className?: string;
  
  /**
   * Custom sx props for the outer wrapper Box
   */
  wrapperSx?: SxProps<Theme>;
  
  /**
   * Whether to show the banner. If not provided, defaults to true when isExistingVendor is true
   */
  showBanner?: boolean;
  
  /**
   * Callback when banner is closed
   */
  onClose?: () => void;
  
  /**
   * Additional condition to show banner (e.g., isVendor check)
   */
  additionalCondition?: boolean;
}

const NewerVersionDocumentsBanner: React.FC<NewerVersionDocumentsBannerProps> = ({
  isExistingVendor = true,
  message,
  className = "newer-version-documents-banner-container",
  wrapperSx,
  showBanner: controlledShowBanner,
  onClose: controlledOnClose,
  additionalCondition = true,
}) => {
  const { languageText } = useLanguageContext();
  const [internalShowBanner, setInternalShowBanner] = useState<boolean>(true);
  
  // Use controlled showBanner if provided, otherwise use internal state
  const showBanner = controlledShowBanner ?? internalShowBanner;
  
  // Use controlled onClose if provided, otherwise use internal handler
  const handleClose = controlledOnClose || (() => setInternalShowBanner(false));
  
  const newerVersionBannerMessage = message || languageText?.ALERTS?.NEWER_VERSION_DOCUMENTS_BANNER ||
    "If you have a newer version of this document, please make sure to upload it here.";
  
  // Don't render if conditions aren't met
  if (!isExistingVendor || !showBanner || !additionalCondition) {
    return null;
  }
  
  const bannerContent = (
    <Box className={className}>
      <Alert
        severity="warning"
        message={newerVersionBannerMessage}
        onClose={handleClose}
      />
    </Box>
  );
  
  // Only wrap in Box if wrapperSx is provided
  if (wrapperSx) {
    return (
      <Box sx={wrapperSx}>
        {bannerContent}
      </Box>
    );
  }
  
  return bannerContent;
};

export default NewerVersionDocumentsBanner;
